import { REGIONS, getRegionById } from './regions';

/**
 * Destinations are the core unit of the site — tours and content link to location ids.
 */
export const LOCATIONS = [
  {
    id: 'serengeti',
    name: 'Serengeti National Park',
    regionId: 'northern',
    coordinates: { lat: -2.333, lng: 34.833 },
    tags: ['safari', 'wildlife', 'great-migration'],
    summary:
      'Endless plains and Africa’s greatest wildlife spectacle including the annual wildebeest migration.',
  },
  {
    id: 'ngorongoro',
    name: 'Ngorongoro Conservation Area',
    regionId: 'northern',
    coordinates: { lat: -3.2, lng: 35.5 },
    tags: ['safari', 'crater', 'wildlife'],
    summary:
      'A UNESCO World Heritage site with the world’s largest intact volcanic caldera teeming with wildlife.',
  },
  {
    id: 'kilimanjaro',
    name: 'Mount Kilimanjaro',
    regionId: 'northern',
    coordinates: { lat: -3.067, lng: 37.35 },
    tags: ['trekking', 'adventure', 'mountain'],
    summary:
      'Africa’s highest peak multi-day climbs through rainforest, moorland, and alpine desert.',
  },
  {
    id: 'tarangire',
    name: 'Tarangire National Park',
    regionId: 'northern',
    coordinates: { lat: -3.833, lng: 36.0 },
    tags: ['safari', 'elephants', 'wildlife'],
    summary:
      'Famous for large elephant herds, baobab landscapes, and excellent dry-season game viewing.',
  },
  {
    id: 'lake-manyara',
    name: 'Lake Manyara National Park',
    regionId: 'northern',
    coordinates: { lat: -3.5, lng: 35.8 },
    tags: ['safari', 'birds', 'wildlife'],
    summary:
      'Compact park known for tree-climbing lions, flamingos, and diverse birdlife along the Rift Valley.',
  },
  {
    id: 'arusha',
    name: 'Arusha',
    regionId: 'northern',
    coordinates: { lat: -3.3869, lng: 36.683 },
    tags: ['city', 'gateway'],
    summary:
      'Safari gateway at the foot of Mount Meru ideal for pre- and post-tour stays.',
  },
  {
    id: 'ruaha',
    name: 'Ruaha National Park',
    regionId: 'southern',
    coordinates: { lat: -7.5, lng: 34.8 },
    tags: ['safari', 'wildlife', 'remote'],
    summary:
      'Tanzania’s largest national park rugged wilderness with lions, elephants, and wild dogs.',
  },
  {
    id: 'nyerere',
    name: 'Nyerere National Park',
    regionId: 'southern',
    coordinates: { lat: -7.8, lng: 38.5 },
    tags: ['safari', 'boat-safari', 'wildlife'],
    summary:
      'Formerly Selous vast reserve with Rufiji River boat safaris and walking tours.',
  },
  {
    id: 'mikumi',
    name: 'Mikumi National Park',
    regionId: 'southern',
    coordinates: { lat: -7.4, lng: 37.0 },
    tags: ['safari', 'wildlife'],
    summary:
      'Accessible southern park on the highway to Dar es Salaam strong wildlife year-round.',
  },
  {
    id: 'udzungwa',
    name: 'Udzungwa Mountains National Park',
    regionId: 'southern',
    coordinates: { lat: -7.85, lng: 36.85 },
    tags: ['trekking', 'rainforest', 'primates'],
    summary:
      'Eastern Arc rainforest hike to Sanje Falls and track endemic primates and birds.',
  },
  {
    id: 'mahale',
    name: 'Mahale Mountains National Park',
    regionId: 'western',
    coordinates: { lat: -6.25, lng: 29.95 },
    tags: ['chimpanzees', 'trekking', 'lake'],
    summary:
      'Remote park on Lake Tanganyika trek to observe wild chimpanzees in forested mountains.',
  },
  {
    id: 'gombe',
    name: 'Gombe Stream National Park',
    regionId: 'western',
    coordinates: { lat: -4.65, lng: 29.583 },
    tags: ['chimpanzees', 'trekking'],
    summary:
      'Jane Goodall’s research site intimate chimpanzee trekking along Lake Tanganyika.',
  },
  {
    id: 'zanzibar',
    name: 'Zanzibar',
    regionId: 'coastal',
    coordinates: { lat: -6.165, lng: 39.199 },
    tags: ['beach', 'culture', 'island'],
    summary:
      'Historic Stone Town, spice farms, and white-sand beaches perfect after a safari.',
  },
  {
    id: 'dar-es-salaam',
    name: 'Dar es Salaam',
    regionId: 'coastal',
    coordinates: { lat: -6.792, lng: 39.208 },
    tags: ['city', 'gateway'],
    summary:
      'Tanzania’s commercial capital and main international gateway to the coast and islands.',
  },
];

export function getLocationById(id) {
  return LOCATIONS.find((location) => location.id === id);
}

export function getLocationsByRegion(regionId) {
  return LOCATIONS.filter((location) => location.regionId === regionId);
}

const ISLAND_REGION_IDS = ['coastal'];

/** Parks and cities on the Tanzanian mainland */
export function getMainlandLocations() {
  return LOCATIONS.filter((location) => !ISLAND_REGION_IDS.includes(location.regionId));
}

/** Zanzibar, coast, and island destinations */
export function getIslandLocations() {
  return LOCATIONS.filter((location) => ISLAND_REGION_IDS.includes(location.regionId));
}

export function getDestinationZoneHash(location) {
  return ISLAND_REGION_IDS.includes(location.regionId)
    ? '#tanzania-island'
    : '#tanzania-mainland';
}

/** Locations grouped with their region metadata — for browse-by-location UI */
export function getLocationsGroupedByRegion() {
  return REGIONS.map((region) => ({
    region,
    locations: getLocationsByRegion(region.id),
  })).filter((group) => group.locations.length > 0);
}

export { REGIONS, getRegionById };
